/** The screen widths the owner can preview their site at. */
export type PreviewSize = "desktop" | "tablet" | "phone";

export const PREVIEW_SIZES: Array<{ size: PreviewSize; label: string }> = [
  { size: "desktop", label: "Desktop" },
  { size: "tablet", label: "Tablet" },
  { size: "phone", label: "Phone" },
];

export const PREVIEW_WIDTHS: Record<PreviewSize, number> = { desktop: 1280, tablet: 768, phone: 390 };

/**
 * The tallest a screen of each size gets. Shrunk to fit a phone, a desktop page would otherwise be a
 * window thousands of pixels tall, and sections sized to the screen's height would stretch to match.
 */
const PREVIEW_MAX_HEIGHTS: Record<PreviewSize, number> = { desktop: 900, tablet: 1024, phone: Infinity };

export interface PreviewFit {
  /** The page's own width and height, in its CSS pixels. */
  width: number;
  height: number;
  /** How much it is shrunk to fit the space; never enlarged. */
  scale: number;
  /** Space to its left, so it sits in the middle. */
  left: number;
}

/**
 * How the page sits in the preview at a size: laid out at that size's width, so it is the layout
 * visitors get, then scaled down (never up) to fit the space and centred. Its height fills the space
 * once scaled, up to a real screen's height. Desktop is at least its width and otherwise fills a wider
 * space, as a big screen would.
 */
export function previewFit(size: PreviewSize, space: { width: number; height: number }): PreviewFit {
  const width = size === "desktop" ? Math.max(PREVIEW_WIDTHS.desktop, space.width) : PREVIEW_WIDTHS[size];
  const scale = space.width > 0 ? Math.min(1, space.width / width) : 1;
  const height = Math.min(Math.max(0, space.height) / scale, Math.max(PREVIEW_MAX_HEIGHTS[size], space.height));
  return { width, height, scale, left: Math.max(0, (space.width - width * scale) / 2) };
}

/** The size a preview opens at: the viewer's own kind of screen. */
export function startingPreviewSize(wide: boolean): PreviewSize {
  return wide ? "desktop" : "phone";
}
